import { Header } from "@/components/Header";
import { useSEO } from "@/hooks/use-seo";
import { Footer } from "@/components/Footer";
import { Mail, MessageCircle, Clock, FileText, Users, HelpCircle, ChevronRight } from "lucide-react";

const CONTACT_REASONS = [
  { icon: FileText, title: "Oprava nebo aktualizace recenze", desc: "Zjistili jste, že některá informace v recenzi kasina je zastaralá nebo nesprávná? Dejte nám vědět." },
  { icon: Users, title: "Spolupráce a partnerství", desc: "Jste zástupci online kasina nebo jiné herní platformy a máte zájem o spolupráci? Rádi se o tom pobavíme." },
  { icon: HelpCircle, title: "Obecné dotazy", desc: "Máte otázku k obsahu webu, bonusovým nabídkám nebo jiným tématům? Jsme tu pro vás." },
  { icon: MessageCircle, title: "Zpětná vazba a návrhy", desc: "Víte, jak bychom mohli web vylepšit? Vaše zpětná vazba je pro nás nesmírně cenná." },
];

export default function KontaktPage() {
  useSEO({ title: "Kontakt – Napište týmu HrajBonus | Dotazy a podněty", description: "Máte dotaz nebo chcete nahlásit chybu? Kontaktujte tým HrajBonus e-mailem nebo přes formulář. Odpovídáme do 48 hodin.",
  url: "https://hrajbonus.com/kontakt", });

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f8fc]">
      <Header />

      <div
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 25% 50%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-16 sm:py-20 relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <span className="text-white/60 text-sm font-semibold uppercase tracking-widest">Kontakt</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5 max-w-2xl">
            Kontaktujte nás
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl">
            Rádi vám odpovíme na jakékoli dotazy, přijmeme zpětnou vazbu nebo probereme možnosti spolupráce. Napiště nám — obvykle odpovídáme do jednoho pracovního dne.
          </p>
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-20 flex-1 space-y-16">

        <section>
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
                <Mail className="w-3.5 h-3.5" /> Přímý kontakt
              </span>
              <h2 className="text-3xl font-black text-gray-900 mb-4">Napište nám přímo</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Nejrychlejší způsob, jak se s námi spojit, je e-mail. Snažíme se odpovídat na všechny zprávy do jednoho pracovního dne, nejpozději do 48 hodin.
              </p>

              <div className="space-y-4">
                <a
                  href="mailto:info@aroundcasinos.com"
                  className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-[#dde4ee] shadow-sm hover:border-[#003da5] transition-all group"
                  data-testid="link-contact-email-main"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-0.5">E-mail</div>
                    <div className="text-[#003da5] font-bold group-hover:underline">info@aroundcasinos.com</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#003da5] transition-colors" />
                </a>

                <div className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-[#dde4ee] shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#f3f6fb] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#003da5]" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-0.5">Doba odezvy</div>
                    <div className="text-gray-800 font-semibold">Do 24–48 hodin</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-amber-800 text-sm leading-relaxed">
                  <strong>Poznámka:</strong> Neposkytujeme zákaznickou podporu kasin. Pro dotazy ke svému hernímu účtu, bonusům nebo platbám kontaktujte přímo konkrétní kasino přes jeho zákaznický servis.
                </p>
              </div>
            </div>

            <div>
              <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
                <MessageCircle className="w-3.5 h-3.5" /> Kdy nás kontaktovat
              </span>
              <h2 className="text-3xl font-black text-gray-900 mb-6">Jak vám můžeme pomoci</h2>
              <div className="space-y-4">
                {CONTACT_REASONS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#dde4ee] shadow-sm">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#f3f6fb]">
                        <Icon className="w-4.5 h-4.5 text-[#003da5]" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1">{item.title}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="bg-white rounded-3xl border border-[#dde4ee] shadow-sm p-8 sm:p-12">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
                  <MessageCircle className="w-3.5 h-3.5" /> Kontaktní formulář
                </span>
                <h2 className="text-3xl font-black text-gray-900">Napište nám zprávu</h2>
                <p className="text-gray-500 text-sm mt-2">Vyplňte formulář níže a my se vám co nejdříve ozveme.</p>
              </div>
              <form
                onSubmit={(e) => { e.preventDefault(); window.location.href = "mailto:info@aroundcasinos.com"; }}
                className="space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Jméno a příjmení</label>
                    <input
                      type="text"
                      placeholder="Jan Novák"
                      className="w-full px-4 py-3 rounded-xl border border-[#dde4ee] text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:border-[#003da5] focus:ring-2 focus:ring-[#003da5]/10 transition-all"
                      data-testid="input-contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">E-mailová adresa</label>
                    <input
                      type="email"
                      placeholder="jan@priklad.cz"
                      className="w-full px-4 py-3 rounded-xl border border-[#dde4ee] text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:border-[#003da5] focus:ring-2 focus:ring-[#003da5]/10 transition-all"
                      data-testid="input-contact-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Předmět</label>
                  <select
                    className="w-full px-4 py-3 rounded-xl border border-[#dde4ee] text-gray-900 text-sm focus:outline-none focus:border-[#003da5] focus:ring-2 focus:ring-[#003da5]/10 transition-all bg-white"
                    data-testid="select-contact-subject"
                  >
                    <option value="">Vyberte téma</option>
                    <option>Oprava nebo aktualizace recenze</option>
                    <option>Spolupráce a partnerství</option>
                    <option>Zpětná vazba a návrhy</option>
                    <option>Obecný dotaz</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Zpráva</label>
                  <textarea
                    rows={5}
                    placeholder="Napište nám, s čím vám můžeme pomoci..."
                    className="w-full px-4 py-3 rounded-xl border border-[#dde4ee] text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:border-[#003da5] focus:ring-2 focus:ring-[#003da5]/10 transition-all resize-none"
                    data-testid="textarea-contact-message"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl font-bold text-white transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #003da5, #002d7c)", boxShadow: "0 4px 14px rgba(0,61,165,0.25)" }}
                  data-testid="button-contact-submit"
                >
                  Odeslat zprávu
                </button>
                <p className="text-center text-gray-400 text-xs">
                  Odesláním formuláře souhlasíte s našimi{" "}
                  <a href="/zasady-ochrany-osobnich-udaju" className="text-[#003da5] hover:underline">Zásadami ochrany osobních údajů</a>.
                </p>
              </form>
            </div>
          </div>
        </section>

        <section>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "O nás", desc: "Zjistěte více o týmu za hrajbonus.com a naší misi.", href: "/o-nas", label: "Přejít na O nás" },
              { title: "Naši autoři", desc: "Poznejte odborníky, kteří stojí za recenzemi kasin.", href: "/nasi-autori", label: "Poznat tým" },
              { title: "Redakční metodologie", desc: "Zjistěte, jak přistupujeme k hodnocení a testování kasin.", href: "/redakcni-metodologie", label: "Číst metodologii" },
            ].map((link) => (
              <a
                key={link.title}
                href={link.href}
                className="flex flex-col p-6 bg-white rounded-2xl border border-[#dde4ee] shadow-sm hover:border-[#003da5] hover:shadow-md transition-all group"
                data-testid={`link-footer-${link.href.replace("/", "")}`}
              >
                <h3 className="text-base font-bold text-gray-900 mb-2">{link.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4">{link.desc}</p>
                <span className="text-[#003da5] text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                  {link.label} <ChevronRight className="w-3.5 h-3.5" />
                </span>
              </a>
            ))}
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
